import React from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Facebook, Instagram } from "lucide-react";
import { ASSETS, SITE } from "@/constants/site";

export default function Footer() {
    return (
        <footer
            data-testid="site-footer"
            className="bg-[#0A192F] text-white"
        >
            <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-20">
                <div className="grid lg:grid-cols-12 gap-12">
                    <div className="lg:col-span-5">
                        <div className="flex items-center gap-3 mb-6">
                            <div className="bg-white p-2 rounded-sm">
                                <img
                                    src={ASSETS.logo}
                                    alt="HWC"
                                    className="h-12 w-auto"
                                />
                            </div>
                            <div className="leading-tight">
                                <div className="font-serif text-2xl font-bold">
                                    Hopewell Consulting
                                </div>
                                <div className="text-[10px] tracking-[0.3em] uppercase text-[#D4AF37]">
                                    Turning Vision Into Reality
                                </div>
                            </div>
                        </div>
                        <p className="text-slate-300 leading-relaxed max-w-md mb-8">
                            Knoxville-based consulting firm helping leaders,
                            organizations, and entrepreneurs build with
                            structure, strategy, and results.
                        </p>
                        <div className="flex items-center gap-3">
                            <a
                                href={SITE.facebook}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="footer-facebook-link"
                                className="w-10 h-10 border border-slate-700 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors flex items-center justify-center rounded-sm"
                                aria-label="Facebook"
                            >
                                <Facebook className="w-4 h-4" />
                            </a>
                            <a
                                href={SITE.instagram}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="footer-instagram-link"
                                className="w-10 h-10 border border-slate-700 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors flex items-center justify-center rounded-sm"
                                aria-label="Instagram"
                            >
                                <Instagram className="w-4 h-4" />
                            </a>
                        </div>
                    </div>

                    <div className="lg:col-span-3">
                        <h4 className="text-[11px] tracking-[0.25em] uppercase text-[#D4AF37] font-semibold mb-5">
                            Services
                        </h4>
                        <ul className="space-y-3 text-slate-300 text-sm">
                            <li>
                                <Link
                                    to="/services"
                                    data-testid="footer-link-business-startup"
                                    className="hover:text-white"
                                >
                                    Business Start-Up
                                </Link>
                            </li>
                            <li>
                                <Link
                                    to="/nonprofit"
                                    data-testid="footer-link-nonprofit"
                                    className="hover:text-white"
                                >
                                    Nonprofit Development
                                </Link>
                            </li>
                            <li>
                                <Link
                                    to="/new-construction"
                                    data-testid="footer-link-new-construction"
                                    className="hover:text-white"
                                >
                                    New Construction
                                </Link>
                            </li>
                            <li>
                                <Link
                                    to="/services"
                                    data-testid="footer-link-grant-writing"
                                    className="hover:text-white"
                                >
                                    Grant Writing
                                </Link>
                            </li>
                            <li>
                                <Link
                                    to="/services"
                                    data-testid="footer-link-branding"
                                    className="hover:text-white"
                                >
                                    Branding & Marketing
                                </Link>
                            </li>
                        </ul>
                    </div>

                    <div className="lg:col-span-2">
                        <h4 className="text-[11px] tracking-[0.25em] uppercase text-[#D4AF37] font-semibold mb-5">
                            Company
                        </h4>
                        <ul className="space-y-3 text-slate-300 text-sm">
                            <li>
                                <Link to="/about" className="hover:text-white">About</Link>
                            </li>
                            <li>
                                <Link to="/impact" className="hover:text-white">Impact</Link>
                            </li>
                            <li>
                                <Link to="/blog" className="hover:text-white">Insights</Link>
                            </li>
                            <li>
                                <Link to="/contact" className="hover:text-white">Contact</Link>
                            </li>
                            <li>
                                <Link to="/login" data-testid="footer-owner-login" className="hover:text-[#D4AF37] text-slate-500">Owner Login</Link>
                            </li>
                        </ul>
                    </div>

                    <div className="lg:col-span-2">
                        <h4 className="text-[11px] tracking-[0.25em] uppercase text-[#D4AF37] font-semibold mb-5">
                            Contact
                        </h4>
                        <ul className="space-y-4 text-slate-300 text-sm">
                            <li>
                                <a
                                    href={`tel:${SITE.phoneLink}`}
                                    data-testid="footer-phone-link"
                                    className="flex items-start gap-2 hover:text-white"
                                >
                                    <Phone className="w-4 h-4 mt-0.5 text-[#D4AF37]" />
                                    {SITE.phone}
                                </a>
                            </li>
                            <li>
                                <a
                                    href={`mailto:${SITE.email}`}
                                    data-testid="footer-email-link"
                                    className="flex items-start gap-2 hover:text-white break-all"
                                >
                                    <Mail className="w-4 h-4 mt-0.5 text-[#D4AF37]" />
                                    {SITE.email}
                                </a>
                            </li>
                            <li className="flex items-start gap-2">
                                <MapPin className="w-4 h-4 mt-0.5 text-[#D4AF37]" />
                                {SITE.address}
                            </li>
                        </ul>
                    </div>
                </div>
            </div>

            <div className="border-t border-slate-800">
                <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-6 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-slate-500">
                    <p>
                        © {new Date().getFullYear()} Hopewell Consulting LLC.
                        All rights reserved.
                    </p>
                    <div className="flex items-center gap-6">
                        <Link to="/privacy" data-testid="footer-privacy-link" className="hover:text-white">
                            Privacy Policy
                        </Link>
                        <Link to="/terms" data-testid="footer-terms-link" className="hover:text-white">
                            Terms & Conditions
                        </Link>
                        <Link
                            to="/login"
                            data-testid="footer-owner-portal"
                            aria-label="Owner portal"
                            className="text-slate-700 hover:text-[#D4AF37] transition-colors"
                            title="Owner Portal"
                        >
                            ·
                        </Link>
                    </div>
                </div>
            </div>
        </footer>
    );
}
